/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import intl from 'react-intl-universal'
import { useSelector, useDispatch } from 'react-redux'
import { parseISO } from 'date-fns'

import { errorAction } from '~/redux/commonActions'
import { syncEditorContent } from '~/redux/draftEditor'
import { EllipsisReduxStore } from '~/types'
import LocaleUtils from '~/reactapp/shared/DatePicker/localeUtils'
import { en as publishingLocaleData } from '~/modules/publishActions/locale.js'
import { setScheduleStatus } from '~/redux/storyData'
import { getStoryOperationInProgress } from '~/redux/appReducer'

import { publishPermissions } from './publishPermissions'
import UnscheduleModal from './UnscheduleModal'
import UnpublishModal from './UnpublishModal'
import { PublishErrorProps, JobType } from './types'
import PublishModal from './PublishModal'
import { getScheduledEvents } from '~/api/story'
import ClonedStories from './ClonedStories'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cx = require('./styles.scss')

type ModalType =
  | 'publish'
  | 'unpublish'
  | 'unschedule-publish'
  | 'unschedule-unpublish'
  | 'none'

export function getDateTimeFormat(
  clockFormat: EllipsisReduxStore['settings']['clockFormat']
) {
  return `${LocaleUtils.locale.formatLong.date({
    width: 'medium',
  })} ${clockFormat === '12' ? 'h:mm:ss a' : 'HH:mm:ss'}`
}

export function Actions() {
  const dispatch = useDispatch()
  const {
    story,
    isValid,
    validationMsg,
    timezone,
    clockFormat,
    scheduledPublishDate,
    scheduledExpirationDate,
  } = useSelector((state: EllipsisReduxStore) => ({
    story: state.storyData.story,
    isValid: state.storyData.isValid,
    validationMsg: state.storyData.validationMsg,
    timezone: state.settings.timezone,
    clockFormat: state.settings.clockFormat,
    scheduledPublishDate: state.storyData.scheduleStatus.publish
      ? parseISO(state.storyData.scheduleStatus.publish)
      : undefined,
    scheduledExpirationDate: state.storyData.scheduleStatus.unpublish
      ? parseISO(state.storyData.scheduleStatus.unpublish)
      : undefined,
  }))
  const circulations = useSelector(
    (state: EllipsisReduxStore) => state.storyData.circulations
  )
  const isStoryOperationInProgress = useSelector((state: EllipsisReduxStore) =>
    getStoryOperationInProgress(state)
  )
  const [modalType, setModalType] = React.useState<ModalType>('none')
  const [fetchScheduledError, setFetchScheduledError] = React.useState<boolean>(
    false
  )

  LocaleUtils.timeZone = timezone

  const fetchScheduledEvents = React.useCallback(async () => {
    if (!story?._id) {
      return
    }

    try {
      const scheduledEvents = await getScheduledEvents(story._id)

      dispatch(
        setScheduleStatus({
          publish: scheduledEvents.schedule.find(
            (job) => job.job_type === JobType.PUBLISH
          )?.date,
          unpublish: scheduledEvents.schedule.find(
            (job) => job.job_type === JobType.UNPUBLISH
          )?.date,
        })
      )
      setFetchScheduledError(false)
    } catch (error) {
      setFetchScheduledError(true)
    }
  }, [dispatch, story?._id])

  React.useEffect(() => {
    fetchScheduledEvents()
  }, [fetchScheduledEvents])

  if (!story) {
    return null
  }

  const { canPublish, canUnpublish, canUnschedule } = publishPermissions(story)
  const isPublished = story.additional_properties
    ? story.additional_properties.is_published
    : false
  const publishedDate = story.publish_date
  const additionalPublishedDate = story.additional_properties
    ? story.additional_properties.publish_date
    : undefined

  const onError: PublishErrorProps['handleError'] = (errorKey) => {
    const findParentKey = (errorKey) => {
      const parentKey = Object.keys(publishingLocaleData.publishActions).filter(
        (parent) => {
          if (publishingLocaleData.publishActions[parent][errorKey]) {
            return parent
          }
        }
      )
      return parentKey[0]
    }
    dispatch(
      errorAction({
        message:
          intl.get(`PublishActions.apiErrors.${errorKey}`) ||
          intl.get(`publishActions.${findParentKey(errorKey)}.${errorKey}`) ||
          intl.get(`errorReasons.${errorKey}`) ||
          errorKey,
      })
    )
  }

  const DATEFORMAT = getDateTimeFormat(clockFormat)

  function openModal(modalType: ModalType) {
    if (modalType === 'publish') {
      dispatch(syncEditorContent())
    }
    setModalType(modalType)
  }

  let publishButtonTooltip = validationMsg
  if (!circulations.length) {
    publishButtonTooltip = intl.get('PublishActions.missingCirculationsTooltip')
  }

  return (
    <div className="nav">
      {canPublish && (
        <div className="nav-item pr-2">
          {!scheduledPublishDate && (
            <>
              {modalType === 'publish' && (
                <PublishModal
                  story={story}
                  handleError={onError}
                  onClose={() => setModalType('none')}
                  fetchScheduledEvents={fetchScheduledEvents}
                />
              )}
              <button
                className="btn btn-success"
                data-testid={isPublished ? 'btn-update' : 'btn-publish'}
                disabled={
                  isStoryOperationInProgress || !isValid || !circulations.length
                }
                onClick={() => openModal('publish')}
              >
                {isPublished
                  ? intl.get('publishActions.panel.updateButton')
                  : intl.get('publishActions.panel.publishButton')}
                {publishButtonTooltip && (
                  <i
                    style={{ marginLeft: '5px' }}
                    className="fa fa-info-circle fa-lg"
                    data-testid="publish-validation-message"
                    data-toggle="tooltip"
                    data-placement="right"
                    data-original-title={publishButtonTooltip}
                    title={publishButtonTooltip}
                  />
                )}
              </button>
            </>
          )}

          {canUnpublish && isPublished && !scheduledExpirationDate && (
            <>
              {modalType === 'unpublish' && (
                <UnpublishModal
                  story={story}
                  defaultTime={LocaleUtils.relativeNow()}
                  handleError={onError}
                  // In a draft version of a story, the publishedDate can be:
                  // 1. sheduled publish date if it has been scheduled for another publish
                  // 2. current publish date
                  // 3. missing or old dates since only the published edition is guaranteed to have it, not the draft version we fetched from the Story API
                  // So explicitly use the corresponding values: scheduledPublishDate, additionalPublishedDate, and publishedDate (fallback) here
                  validationDate={
                    scheduledPublishDate ||
                    parseISO(
                      (additionalPublishedDate || publishedDate) as string
                    )
                  }
                  use24Hour={clockFormat === '24'}
                  onClose={() => setModalType('none')}
                  fetchScheduledEvents={fetchScheduledEvents}
                />
              )}
              <button
                style={{ marginLeft: '8px' }}
                className="btn btn-danger"
                onClick={() => openModal('unpublish')}
                disabled={isStoryOperationInProgress}
                data-testid="btn-unpublish-story"
              >
                {intl.get('publishActions.panel.unpublish')}
              </button>
            </>
          )}
        </div>
      )}

      <div className={`nav-item pt-1 align-baseline ${cx.clonedSection}`}>
        {isPublished && additionalPublishedDate && (
          <div className="text-muted">
            {intl.get('publishActions.panel.lastPublishedLocal')}:&nbsp;
            {LocaleUtils.format(additionalPublishedDate as string, DATEFORMAT)}
          </div>
        )}

        {scheduledPublishDate && (
          <>
            {!scheduledExpirationDate && !isPublished && (
              <>
                {modalType === 'unpublish' && (
                  <UnpublishModal
                    story={story}
                    defaultTime={LocaleUtils.relativeDate(scheduledPublishDate)}
                    // In a draft version of a story, the publishedDate can be:
                    // 1. sheduled publish date
                    // 2. missing or old dates  since only the published edition is guaranteed to have it, not the draft version we fetched from the Story API
                    // So explicitly use the corresponding values: scheduledPublishDate and publishedDate (fallback) here
                    validationDate={
                      scheduledPublishDate || parseISO(publishedDate as string)
                    }
                    handleError={onError}
                    use24Hour={clockFormat === '24'}
                    onClose={() => setModalType('none')}
                    fetchScheduledEvents={fetchScheduledEvents}
                  />
                )}
                <button
                  className="btn btn-danger mr-2"
                  onClick={() => openModal('unpublish')}
                  disabled={isStoryOperationInProgress}
                >
                  {intl.get(
                    'publishActions.unpublishModal.scheduleUnpublishButton'
                  )}
                </button>
              </>
            )}

            {fetchScheduledError && (
              <span className="text-muted">
                {intl.get('publishActions.panel.scheduledPublishStatusError')}
              </span>
            )}

            {!fetchScheduledError && canPublish && (
              <>
                {modalType === 'publish' && (
                  <PublishModal
                    story={story}
                    scheduledDate={scheduledPublishDate}
                    handleError={onError}
                    onClose={() => setModalType('none')}
                    fetchScheduledEvents={fetchScheduledEvents}
                  />
                )}

                <span className="text-muted">
                  {intl.get('publishActions.panel.scheduledPublishRecentSave')}
                  <button
                    className={`btn btn-link ${cx.updateScheduleBtn}`}
                    {...(!isValid && {
                      'data-toggle': 'tooltip',
                      'data-placement': 'right',
                      'data-original-title': publishButtonTooltip,
                      title: publishButtonTooltip,
                    })}
                    onClick={() => openModal('publish')}
                    disabled={isStoryOperationInProgress}
                  >
                    {LocaleUtils.format(scheduledPublishDate, DATEFORMAT)}
                    &nbsp;
                    <i className="fa fa-edit" />
                  </button>
                </span>
              </>
            )}
            {canUnschedule && (
              <>
                {modalType === 'unschedule-publish' && (
                  <UnscheduleModal
                    story={story}
                    handleError={onError}
                    jobType={JobType.PUBLISH}
                    scheduledExpirationDate={scheduledExpirationDate}
                    onClose={() => setModalType('none')}
                    fetchScheduledEvents={fetchScheduledEvents}
                  />
                )}
                <button
                  data-testid={`unschedule-${JobType.PUBLISH}`}
                  style={{ verticalAlign: 'inherit' }}
                  className="btn btn-link ml-1 text-danger"
                  onClick={() => openModal('unschedule-publish')}
                  disabled={isStoryOperationInProgress}
                >
                  {intl.get('publishActions.panel.unschedule')}
                </button>
              </>
            )}
          </>
        )}

        {scheduledExpirationDate && (
          <div>
            {modalType === 'unpublish' && (
              <UnpublishModal
                story={story}
                defaultTime={LocaleUtils.relativeDate(scheduledExpirationDate)}
                // In a draft version of a story, the publishedDate can be:
                // 1. sheduled publish date if it has been scheduled
                // 2. current publish date
                // 3. missing or old dates since only the published edition is guaranteed to have it, not the draft version we fetched from the Story API
                // So explicitly use the corresponding values: scheduledPublishDate, additionalPublishedDate, and publishedDate (fallback) here
                validationDate={
                  scheduledPublishDate ||
                  parseISO((additionalPublishedDate || publishedDate) as string)
                }
                handleError={onError}
                use24Hour={clockFormat === '24'}
                onClose={() => setModalType('none')}
                fetchScheduledEvents={fetchScheduledEvents}
              />
            )}

            <span className="text-muted">
              {intl.get('publishActions.panel.expirationDate')}
              <button
                className={`btn btn-link ${cx.updateScheduleBtn}`}
                onClick={() => openModal('unpublish')}
                disabled={isStoryOperationInProgress}
              >
                &nbsp;
                {LocaleUtils.format(scheduledExpirationDate, DATEFORMAT)}
                &nbsp;
                <i className="fa fa-edit" />
              </button>
            </span>

            {modalType === 'unschedule-unpublish' && (
              <UnscheduleModal
                story={story}
                jobType={JobType.UNPUBLISH}
                scheduledExpirationDate={scheduledExpirationDate}
                handleError={onError}
                onClose={() => setModalType('none')}
                fetchScheduledEvents={fetchScheduledEvents}
              />
            )}

            {canUnschedule && (
              <button
                data-testid={`unschedule-${JobType.UNPUBLISH}`}
                style={{ verticalAlign: 'inherit' }}
                className="btn btn-link ml-1 text-danger"
                onClick={() => openModal('unschedule-unpublish')}
                disabled={isStoryOperationInProgress}
              >
                {intl.get('publishActions.panel.unschedule')}
              </button>
            )}
          </div>
        )}

        <ClonedStories relatedContent={story.related_content} />
      </div>
    </div>
  )
}

/**
 * Any internal errors will cause this component to "disappear", let user
 * know to refresh
 */
export default class PublishActions extends React.Component<
  {},
  { hasError: boolean }
> {
  public constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  public static getDerivedStateFromError(error) {
    console.error(error.message)
    return { hasError: true }
  }

  public render() {
    if (this.state.hasError) {
      return (
        // probably need a translation here?
        <span>
          Something went wrong -{' '}
          <a href="javascript:location.reload()">reload story</a>
        </span>
      )
    }

    return <Actions />
  }
}
